import React from 'react'
import ReactTable from 'react-table-6'
import moment from 'moment'
import { I18n } from 'react-redux-i18n'
import { formatCurrency, formatProfitLoss, formatPrice } from '../lib/currency-formatter'

import { PricesExtended, TradeObject } from '@bdswiss/mt4-connector'
import './PositionsTable.css'
import Spinner from './Spinner'

export interface Props {
  accountLoaded: boolean
  assets: PricesExtended
  positions: TradeObject[]
  currency: string
  locale: string
  tradeOperationEnabled: boolean
  favourites: string[]
  isPending?: boolean
  selectedPosition?: number
  selectedOrder?: number
  onSelectPosition: (order: number, expanded?: boolean) => void
  onCloseAllTrades?: () => void
  onCloseTrade: (order: number, uuid?: string) => void
  onSetChartMode: () => void
  onToggleFavourite: (favourites: string[]) => void
}

export interface State {
  filter: string
  favourites: { [symbol: string]: { confirmed: boolean; operation: 'on' | 'off' } } // true if confirmed from server
}

const PositionsTable = (props: Props) => {
  return <PositionsTableContent {...props} />
}

class PositionsTableContent extends React.Component<Props, State> {
  private columns: any
  private columnsMap: any = {
    favourite: {
      minWidth: 15,
      sortable: false,
      filterable: false,
      className: 'favourite-cell',
      Cell: (props: any) => {
        const favourite = Object.keys(this.state.favourites).find(
          key => key === props.original.symbol
        )
        const confirmed = favourite ? this.state.favourites[favourite].confirmed : true
        return (
          <i
            onClick={() => this.toggleFavourite(props.original.symbol)}
            className={`favourite ${favourite ? 'icon-star-full' : 'icon-star-empty'} ${
              !confirmed ? 'unconfirmed' : ''
            }`}
            title="Favourite"
            data-cy={props.value}
          />
        )
      },
    },
    asset: {
      Header: (props: any) => <span>{I18n.t('asset')}</span>,
      minWidth: 30,
      Cell: (props: any) => {
        const { symbol } = props.original
        const { label } = this.props.assets[symbol] as any
        return (
          <div title={label} className="bdswiss-table--bold">
            {label}
          </div>
        )
      },
    },
    order: {
      Header: (props: any) => <span>{I18n.t('orderId')}</span>,
      minWidth: 30,
      Cell: (props: any) => {
        const { order } = props.original
        return <div title={order}>{order}</div>
      },
    },
    direction: {
      Header: (props: any) => <span>{I18n.t('direction')}</span>,
      minWidth: 20,
      Cell: (props: any) => {
        const { type } = props.original
        return (
          <div
            className={/buy/i.test(type) ? 'bdswiss-table--positive' : 'bdswiss-table--negative'}
          >
            <i className={/buy/i.test(type) ? 'icon-buy-new' : 'icon-sell-new'} />
            {I18n.t(type)}
          </div>
        )
      },
    },
    value: {
      Header: (props: any) => <span>{I18n.t('value')}</span>,
      minWidth: 30,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { volume, symbol } = props.original
        const { locale, assets } = this.props
        const asset = assets[symbol]
        const value = asset
          ? formatCurrency(volume * asset.lotSize, asset.contractType, locale)
          : ''
        return <div title={value}>{value.replace(/0+$/g, '').replace(/\.$/, '.00')}</div>
      },
    },
    investment: {
      Header: (props: any) => <span>{I18n.t('investment')}</span>,
      minWidth: 30,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { investment, volume } = props.original
        const { locale, currency } = this.props
        const invested = investment
          ? formatCurrency(investment, currency, locale)
          : `${volume} ${I18n.t('lots', { count: volume === 1 ? 1 : 0 })}`
        return <div title={invested}>{invested}</div>
      },
    },
    profit: {
      Header: (props: any) => <span>{I18n.t('profit')}</span>,
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { profit } = props.original
        const { currency, locale } = this.props
        const pl = formatProfitLoss(profit, currency, locale, currency === 'CUD' ? {precision: 2} : {})
        return (
          <div title={pl} className={'bdswiss-table--' + (profit > 0 ? 'positive' : 'negative')}>
            {pl}
          </div>
        )
      },
    },
    leverage: {
      Header: (props: any) => <span>{I18n.t('leverage')}</span>,
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { leverage } = props.original
        return leverage ? <div title={`1:${leverage}`}>1:{leverage}</div> : undefined
      },
    },
    openPrice: {
      Header: (props: any) => <span>{I18n.t('openPrice')}&nbsp;&nbsp;&nbsp;&nbsp;</span>,
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const asset = this.props.assets[props.original.symbol]
        const precision = asset ? asset.digits : 5
        const price = formatPrice(props.original.openPrice, precision, '')
        return <div title={price}>{price}&nbsp;&nbsp;&nbsp;&nbsp;</div>
      },
    },
    currentPrice: {
      Header: (props: any) => <span>{I18n.t('currentPrice')}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>,
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { symbol, type } = props.original
        const asset = this.props.assets[symbol]
        const price = asset
          ? formatPrice(/buy/i.test(type) ? asset.mask : asset.mbid, asset.digits, '')
          : '0'
        return <div title={price}>{price}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
      },
    },
    openDate: {
      id: 'openDate',
      accessor: 'openTime',
      Header: (props: any) => <span>{I18n.t('openDate')}</span>,
      minWidth: 60,
      Cell: (props: any) => {
        const { locale } = this.props
        moment.locale(locale)
        const dateTime = moment(props.original.openTime)
        const openDate = `${dateTime.format("MMM DD 'YY")}    ${
          locale === 'en' ? dateTime.format('hh:mmA') : dateTime.format('HH:mm')
        }`
        return <div title={openDate}>{openDate}</div>
      },
    },
    edit: {
      // headerStyle: {display: "none"},
      Header: '',
      width: 80,
      Cell: (props: any) => {
        return (
          <div
            title={I18n.t('edit')}
            className="d-flex align-items-center text-primary"
            onClick={() => this.props.onSelectPosition(props.original.order, true)}
          >
            <i className="icon-edit-new" />
            {I18n.t('edit')}
          </div>
        )
      },
    },
    close: {
      Header: (props: any) => {
        const { positions, onCloseAllTrades } = this.props
        return onCloseAllTrades && positions.length ? (
          <div
            title={I18n.t('closeAll')}
            className="d-flex align-items-center text-danger"
            onClick={onCloseAllTrades}
          >
            <i className="icon-close-new" />
            {I18n.t('closeAll')}
          </div>
        ) : (
          ''
        )
      },
      width: 90,
      Cell: (props: any) => {
        const { order } = props.original
        return (
          <div
            title={I18n.t('close')}
            className="d-flex align-items-center text-primary"
            onClick={this.props.onCloseTrade.bind(null, order, order.uuid)}
          >
            <i className="icon-close-new" />
            {I18n.t('close')}
          </div>
        )
      },
    },
  }

  constructor(props: Props) {
    super(props)

    this.toggleFavourite = this.toggleFavourite.bind(this)

    this.state = {
      filter: 'active',
      favourites: this.props.favourites.reduce(
        (acc, cur) => {
          acc[cur] = { confirmed: true, operation: 'on' }
          return acc
        },
        {} as any
      ),
    }
    let columnNames
    if (this.props.isPending) {
      columnNames = [
        'favourite',
        'asset',
        'order',
        'direction',
        'value',
        'leverage',
        'openPrice',
        'currentPrice',
        'edit',
        'close',
      ]
    } else {
      columnNames = [
        'favourite',
        'asset',
        'order',
        'direction',
        'value',
        'profit',
        'leverage',
        'openPrice',
        'openDate',
        'edit',
        'close',
      ]
    }
    this.columns = columnNames.map((name: string) => this.columnsMap[name])
  }

  public render() {
    if (!this.props.assets || !this.props.accountLoaded) {
      // console.log('Positions pane loading...');
      return <Spinner />
    }
    const {
      positions,
      selectedPosition,
      selectedOrder,
      onSelectPosition,
      onSetChartMode,
    } = this.props

    const data = positions
    return (
      <ReactTable
        noDataText={I18n.t('noItemsToDisplay')}
        data={data}
        columns={this.columns}
        className="bdswiss-table"
        showPagination={false}
        sortable={false}
        resizable={false}
        defaultSorted={[
          {
            id: 'openDate',
            desc: true,
          },
        ]}
        // freezeWhenExpanded={true}
        // collapseOnDataChange={false}
        minRows={15}
        pageSize={data.length}
        getTrProps={(state: any, rowInfo: any, col: any, instance: any) => {
          if (rowInfo) {
            const { order } = rowInfo.original
            const highlight = order === selectedPosition || order === selectedOrder
            return {
              key: order,
              onClick: (e: any) => {
                onSelectPosition(order) // diff[path]);
                onSetChartMode()
              },
              className: highlight ? 'active' : '',
            }
          } else {
            return {}
          }
        }}
      />
    )
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.favourites !== this.props.favourites) {
      this.setState(pState => ({
        favourites: this.props.favourites.reduce(
          (acc, cur) => {
            acc[cur] = { confirmed: true, operation: 'on' }
            return acc
          },
          {} as any
        ),
      }))
    }
  }

  private toggleFavourite(favourite: string) {
    const { favourites } = this.state
    if (Object.keys(favourites).includes(favourite)) {
      favourites[favourite] = { confirmed: false, operation: 'off' }
    } else {
      favourites[favourite] = { confirmed: false, operation: 'on' }
    }
    this.props.onToggleFavourite(
      Object.keys(favourites).filter(key => favourites[key].operation === 'on')
    )
    this.setState(prevState => ({
      favourites,
    }))
  }
}

export default PositionsTable
